import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Building2, Mail, Phone, Globe, FileText, Hash, Tags, UserCog, Briefcase, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";

const inputCls = "h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40";
const inputPlain = "h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40";

function Field({ label, Icon, children, error }) {
    return (
        <div className="space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-foreground">{label}</label>
            <div className="relative">
                {Icon && <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
                {children}
            </div>
            {error && <p className="text-[11.5px] text-[hsl(var(--destructive))]">{error}</p>}
        </div>
    );
}

export default function CustomerNew() {
    const canCreate = useCan("crm.create");
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "", type: "corporate", status: "active", segment: "standard",
        industry: "", website: "", email: "", phone: "",
        tax_number: "", tax_office: "",
        account_manager_id: "", description: "", tags: "",
    });
    const [managers, setManagers] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/employees", { params: { page_size: 200 } });
                setManagers(data.items || []);
            } catch (_) { /* ignore */ }
        })();
    }, []);

    if (!canCreate) return <Forbidden />;

    const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (ev) => {
        ev.preventDefault();
        const e = {};
        if (!form.name.trim()) e.name = tr.validation.required;
        setErrors(e);
        if (Object.keys(e).length) return;
        setSaving(true);
        try {
            // Convert empty strings to null for optional fields to pass backend validation
            const payload = {
                name: form.name,
                type: form.type,
                status: form.status,
                segment: form.segment || null,
                industry: form.industry || null,
                website: form.website || null,
                email: form.email || null,
                phone: form.phone || null,
                tax_number: form.tax_number || null,
                tax_office: form.tax_office || null,
                account_manager_id: form.account_manager_id || null,
                description: form.description || null,
                tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
            };
            const { data } = await api.post("/customers", payload);
            toast.success(tr.crm.create.success);
            navigate(`/customers/${data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Oluşturulamadı.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <Link to="/customers" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground mb-3">
                    <ArrowLeft className="h-3.5 w-3.5" /> {tr.crm.title}
                </Link>
                <h1 className="text-[26px] font-bold tracking-tight">{tr.crm.create.title}</h1>
                <p className="text-[13.5px] text-muted-foreground mt-1">{tr.crm.create.subtitle}</p>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="customer-new-form">
                <section className="card-elevated p-6">
                    <div className="mb-4"><div className="text-[15px] font-semibold">{tr.crm.create.basicInfo}</div></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Müşteri Adı" Icon={Building2} error={errors.name}>
                            <input value={form.name} onChange={on("name")} className={inputCls} data-testid="input-name" />
                        </Field>
                        <Field label="Tür" Icon={Layers}>
                            <select value={form.type} onChange={on("type")} className={inputPlain} data-testid="input-type">
                                <option value="corporate">{tr.crm.types.corporate}</option>
                                <option value="individual">{tr.crm.types.individual}</option>
                            </select>
                        </Field>
                        <Field label="Durum" Icon={Layers}>
                            <select value={form.status} onChange={on("status")} className={inputPlain}>
                                {Object.entries(tr.crm.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label="Segment" Icon={Layers}>
                            <select value={form.segment} onChange={on("segment")} className={inputPlain}>
                                {Object.entries(tr.crm.segments).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label="Sektör" Icon={Briefcase}>
                            <input value={form.industry} onChange={on("industry")} className={inputCls} placeholder="Ör: Moda, Teknoloji…" />
                        </Field>
                        <Field label="Müşteri Temsilcisi" Icon={UserCog}>
                            <select value={form.account_manager_id} onChange={on("account_manager_id")} className={inputPlain} data-testid="input-manager">
                                <option value="">{tr.common.none}</option>
                                {managers.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                            </select>
                        </Field>
                        <div className="md:col-span-2">
                            <Field label={tr.common.description} Icon={FileText}>
                                <textarea value={form.description} onChange={on("description")} rows={3} className="w-full rounded-xl border border-border bg-background pl-10 pr-3 pt-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" placeholder="Müşteri hakkında kısa açıklama…" />
                            </Field>
                        </div>
                        <div className="md:col-span-2">
                            <Field label="Etiketler (virgülle ayırın)" Icon={Tags}>
                                <input value={form.tags} onChange={on("tags")} className={inputCls} placeholder="moda, hazir giyim, premium" />
                            </Field>
                        </div>
                    </div>
                </section>

                <section className="card-elevated p-6">
                    <div className="mb-4"><div className="text-[15px] font-semibold">{tr.crm.create.contactInfo}</div></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label={tr.common.email} Icon={Mail}>
                            <input type="email" value={form.email} onChange={on("email")} className={inputCls} placeholder="info@ornek.com" />
                        </Field>
                        <Field label={tr.common.phone} Icon={Phone}>
                            <input value={form.phone} onChange={on("phone")} className={inputCls} placeholder="+90…" />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Web Sitesi" Icon={Globe}>
                                <input value={form.website} onChange={on("website")} className={inputCls} placeholder="https://…" />
                            </Field>
                        </div>
                    </div>
                </section>

                <section className="card-elevated p-6">
                    <div className="mb-4"><div className="text-[15px] font-semibold">{tr.crm.create.legalInfo}</div></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Vergi Numarası" Icon={Hash}>
                            <input value={form.tax_number} onChange={on("tax_number")} className={inputCls} />
                        </Field>
                        <Field label="Vergi Dairesi" Icon={Building2}>
                            <input value={form.tax_office} onChange={on("tax_office")} className={inputCls} />
                        </Field>
                    </div>
                </section>

                <div className="flex justify-end gap-3">
                    <Link to="/customers" className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</Link>
                    <button type="submit" disabled={saving} className="btn-brand h-11" data-testid="btn-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {tr.common.save}
                    </button>
                </div>
            </form>
        </div>
    );
}
